import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Table,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import searchIcon from "../../../assets/images/search-icon.svg";
import closeIcon from "../../../assets/images/close.svg";
import noSearchIcon from "../../../assets/no-search-found.svg";
import {
  IAgentManagementListProps,
  IAgentManagementUsersNormalized,
} from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import "./AgentManagementList.scss";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import ButtonComponent from "../../common/Button/ButtonComponent";
import { AbilityContext, Can } from "../../../config/Can";
import { CHECK_NUMBER_REGEX, user } from "../../../config/constants";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  getAgentList: any
) => {
  if (val.length >= 3) {
    pageScrollPosition.current = 0;
    getAgentList({
      searchKey: val,
    });
  }
};

const AgentManagementList: React.FunctionComponent<
  IAgentManagementListProps
> = (props: IAgentManagementListProps) => {
  const ability = useContext(AbilityContext);

  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [agentManagementUserData, setData] = useState<
    Array<IAgentManagementUsersNormalized>
  >(props.testAgentManagementUserStateData || []);
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const {
      getAgentList,
      agentManagementUsers,
      isAgentManagementUserAddedorUpdated,
    } = props;
    if (!agentManagementUsers || isAgentManagementUserAddedorUpdated) {
      getAgentList();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, []);

  useEffect(() => {
    const { agentManagementUsers, pagination } = props;
    if (agentManagementUsers) {
      setData(agentManagementUsers);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.agentManagementUsers]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (
      props.isAgentManagementUserUpdated ||
      props.isAgentManagementUserAdded
    ) {
      const message = props.isAgentManagementUserAdded
        ? "AgentManagementUser added successfully!"
        : "AgentManagementUser updated successfully";
      handleToaster(false, message);
    }
  }, [props.isAgentManagementUserAdded, props.isAgentManagementUserUpdated]);

  useEffect(() => {
    if (props.retainedSearchText === "agentCreatedSuccessfully") {
      const message = "Agent added successfully!";
      handleToaster(false, message);
    }
  }, [props.isAgentManagementUserAdded, props.isAgentManagementUserUpdated]);

  useEffect(() => {
    if (props.passwordReset === "passwordResetSuccess") {
      const message = props.passwordReset && "Password sent via SMS!";
      handleToaster(false, message);
    } else if (props.passwordReset === "passwordResetUnsuccessful") {
      const message = props.passwordReset && "Reset password failed!";
      handleToaster(true, message);
    }
  }, [props.passwordReset]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any) =>
        doAPIsearch(value, pageScrollPosition, props.getAgentList),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { getAgentList } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      getAgentList();
    }
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { getAgentList } = props;
    getAgentList();
    setIsSearching(false);
    setSearchTerm("");
  };

  const loadFunc = () => {
    if (agentManagementUserData && !props.isLoading) {
      const { getAgentList, pagination } = props;
      if (pagination) {
        pageScrollPosition.current = window.pageYOffset;
        setLoadMoreText({
          text: "Loading...",
          isLoading: true,
        });
        if (isSearch) {
          getAgentList({
            searchKey: searchTerm,
            page: pagination,
          });
        } else {
          getAgentList({
            page: pagination,
          });
        }
      }
    }
  };

  const editAgentManagementUser = (e: any, index: number) => {
    const { addUpdatePageHandler, userInfo } = props;
    // clears all selected agentList
    let allowUserEdit = false;
    if (userInfo.id === agentManagementUserData[index].agentId) {
      allowUserEdit = ability.can(
        "*",
        new user({ userId: agentManagementUserData[index].agentId })
      );
    } else {
      allowUserEdit =
        ability.can("update", "agent") ||
        ability.can("create", "agent") ||
        ability.can("*", "agent");
    }
    if (allowUserEdit) {
      const agent = {
        ...agentManagementUserData[index],
        zones: agentManagementUserData[index].zones.map((zone) => ({
          ...zone,
          id: (zone as any).zoneId,
        })),
      };

      addUpdatePageHandler(true, agent, searchTerm, window.pageYOffset);
    }
  };

  const renderAgentManagementList = () => {
    return (
      agentManagementUserData! as Array<IAgentManagementUsersNormalized>
    ).map((item, i) => {
      return (
        <tr key={i} onClick={(e) => editAgentManagementUser(e, i)}>
          <td title={item.name}>{item.name}</td>
          <td title={item.phoneNumber || "-"}>{item.phoneNumber || "-"}</td>
          <td title={item.agentTeamName || "-"}>{item.agentTeamName || "-"}</td>
          <td title={item.agentTitleName || "-"}>
            {item.agentTitleName || "-"}
          </td>
          <td>
            <span
              className={
                item.status !== "ACTIVE" ? "deactivated-tag" : "activated-tag"
              }
            >
              {item.status !== "ACTIVE" ? item.status : "active"}
            </span>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="agent-management-data-table-container">
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-12">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchIcon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by name or phone number"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={closeIcon} alt="close" />
              </div>
            </InputGroup>
          </section>
        </section>
      </section>
      {(ability.can("update", "agent") ||
        ability.can("create", "agent") ||
        ability.can("read", "agent") ||
        ability.can("*", "agent")) && (
        <>
          <Table
            borderless
            striped
            className="table"
            id="agent-management-user-list"
          >
            <thead>
              <tr className="table-header-row">
                <th>Agent Name</th>
                <th>Phone number</th>
                <th>Team</th>
                <th>Title</th>
                <th></th>
              </tr>
            </thead>
            {agentManagementUserData && (
              <tbody>{renderAgentManagementList()}</tbody>
            )}
            {props.isLoading && <LoaderComponent />}
            {isSearch &&
            agentManagementUserData &&
            agentManagementUserData.length === 0 ? (
              <tfoot>
                <tr>
                  <div className="no-search-found">
                    <img src={noSearchIcon} alt="no search found" />
                    <div className="no-search-title">No results found</div>
                    <div className="no-search-sub-title">
                      There’s no user by that name. Did you double check?
                    </div>
                    {searchTerm.length === 11 &&
                      searchTerm.match(CHECK_NUMBER_REGEX) && (
                        <div className="mt-4">
                          {(ability.can("*", "agent") ||
                            ability.can("create", "agent")) && (
                            <ButtonComponent
                              buttonText="Add Agent"
                              onClickHandler={() =>
                                props.addUpdatePageHandler(
                                  false,
                                  null,
                                  searchTerm,
                                  window.pageYOffset
                                )
                              }
                            />
                          )}
                        </div>
                      )}
                  </div>
                </tr>
              </tfoot>
            ) : (
              ""
            )}
          </Table>
          {isLoadMore && (
            <div id="load-more-container">
              <ButtonComponent
                buttonText={loadMoreText.text}
                buttonClass="btn-primary-outline button-medium"
                onClickHandler={loadFunc}
                isDisable={loadMoreText.isLoading}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgentManagementList;
